import { forwardRef, type ComponentType, type SVGProps } from 'react'

export type IconRegistry = Record<string, ComponentType<SVGProps<SVGSVGElement>>>

export function createUIIcon<T extends IconRegistry>(iconTypes: T) {
  type UIIconType = keyof T & string

  interface UIIconProps extends React.ComponentProps<'svg'> {
    icon: UIIconType
  }

  const UIIcon = forwardRef<SVGSVGElement, UIIconProps>(({ icon, className, ...props }, ref) => {
    const Icon: ComponentType<SVGProps<SVGSVGElement>> = iconTypes[icon]

    if (!Icon) {
      if (import.meta.env.DEV) {
        console.warn(`UIIcon: unknown icon "${icon}". Available icons: ${Object.keys(iconTypes).join(', ')}`)
      }
      return null
    }

    const currentStyle = `UIIcon--${icon}`

    return (
      <Icon
        aria-hidden='true'
        ref={ref}
        className={['UIIcon', currentStyle, className].filter(Boolean).join(' ')}
        {...props}
      />
    )
  })

  UIIcon.displayName = `UIIcon(${Object.keys(iconTypes).slice(0, 3).join('|')}${Object.keys(iconTypes).length > 3 ? '...' : ''})`

  function isIcon(icon: string): icon is UIIconType {
    return Object.hasOwn(iconTypes, icon)
  }

  return { UIIcon, isIcon }
}
